import {
  AccountProjectionSchema,
  CreateWorkspaceCommandSchema,
  FocusReadinessProjectionSchema,
  IdentityProjectionSchema,
  UpdateOwnAccountPreferencesCommandSchema,
  WorkspaceListSchema,
  WorkspaceProjectionSchema,
  UuidSchema,
  type AccountProjection,
  type FocusReadinessProjection,
  type WorkspaceList,
  type WorkspaceProjection,
} from "@mind/contracts/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { i18n } from "../i18n/index.js";
import {
  endSimulation as endSimulationSession,
  getBrowserSession,
  signOut as signOutSession,
} from "../auth/index.js";
import {
  AppRealtimeProvider,
  createAppRealtimeStore,
  type AppRealtimeContext,
  type AppRealtimeStore,
} from "../realtime/index.js";
import { ApiError, apiRequest } from "../api/client.js";

export type AppIdentityProjection = {
  readonly actor: AccountProjection;
  readonly effective: AccountProjection;
  readonly simulation: {
    readonly id: string;
    readonly actorAccountId: string;
    readonly effectiveAccountId: string;
    readonly allowedWorkspaceIds: readonly string[];
    readonly authorizationEpoch: string;
    readonly expiresAt: string;
  } | null;
};

const EMPTY_WORKSPACES: readonly WorkspaceProjection[] = [];

export type AppBootstrap = {
  readonly identity: AppIdentityProjection;
  readonly workspaces: WorkspaceList;
  readonly readiness: FocusReadinessProjection;
  readonly initiallySelectedWorkspaceId: string | null;
};

export type AppBootstrapReaders = {
  readonly readSession: () => Promise<boolean>;
  readonly readIdentity: () => Promise<AppIdentityProjection>;
  readonly readWorkspaces: () => Promise<WorkspaceList>;
  readonly readReadiness: () => Promise<FocusReadinessProjection>;
};


export async function hydrateAppSession(
  readers: AppBootstrapReaders,
  requestedWorkspaceId?: string,
): Promise<AppBootstrap> {
  await readers.readSession();
  const identity = await readers.readIdentity();
  const workspaces = await readers.readWorkspaces();
  const selectedWorkspace = workspaces.items.find((workspace) => workspace.id === requestedWorkspaceId)
    ?? workspaces.items[0]
    ?? null;
  const readiness = await readers.readReadiness();
  return {
    identity,
    workspaces,
    readiness,
    initiallySelectedWorkspaceId: selectedWorkspace?.id ?? null,
  };
}

export type PreferenceCommand = {
  readonly commandId: string;
  readonly expectedRevision: string;
  readonly expectedSettingsRevision: string;
  readonly uiLocale: AccountProjection["uiLocale"];
  readonly reducedMotion: boolean;
};

export function createPreferenceCommand(
  account: AccountProjection,
  uiLocale: AccountProjection["uiLocale"],
  reducedMotion: boolean,
  commandId: string = crypto.randomUUID(),
): PreferenceCommand {
  return UpdateOwnAccountPreferencesCommandSchema.parse({
    commandId,
    expectedRevision: account.revision,
    expectedSettingsRevision: account.settingsRevision,
    uiLocale,
    reducedMotion,
  });
}
export type CreateWorkspaceCommand = {
  readonly commandId: string;
  readonly workspaceId: string;
  readonly name: string;
};

export function createWorkspaceCommand(
  name: string,
  commandId: string = crypto.randomUUID(),
  workspaceId: string = crypto.randomUUID(),
): CreateWorkspaceCommand {
  return CreateWorkspaceCommandSchema.parse({ commandId, workspaceId, name });
}

export function mergeCreatedWorkspace(
  current: AppBootstrap,
  workspace: WorkspaceProjection,
): AppBootstrap {
  return {
    ...current,
    workspaces: {
      items: [
        ...current.workspaces.items.filter((item) => item.id !== workspace.id),
        workspace,
      ],
    },
    initiallySelectedWorkspaceId: workspace.id,
  };
}

export type AppSessionPhase = "booting" | "authenticated" | "anonymous" | "error";

export type AppSessionContextValue = {
  readonly phase: AppSessionPhase;
  readonly identity: AppIdentityProjection | null;
  readonly workspaces: readonly WorkspaceProjection[];
  readonly selectedWorkspace: WorkspaceProjection | null;
  readonly readiness: FocusReadinessProjection | null;
  readonly error: unknown;
  readonly preferencePending: boolean;
  readonly preferenceError: unknown;
  readonly workspaceCreatePending: boolean;
  readonly workspaceCreateError: unknown;
  readonly restartIdentity: () => Promise<void>;
  readonly retry: () => Promise<void>;
  readonly createWorkspace: (name: string) => Promise<WorkspaceProjection>;
  readonly updatePreferences: (
    uiLocale: AccountProjection["uiLocale"],
    reducedMotion: boolean,
  ) => Promise<AccountProjection>;
  readonly signOut: () => Promise<void>;
  readonly endSimulation: () => Promise<void>;
};

const AppSessionContext = createContext<AppSessionContextValue | null>(null);

export function useAppSession(): AppSessionContextValue {
  const value = useContext(AppSessionContext);
  if (value === null) throw new Error("useAppSession must be used within AppSessionProvider");
  return value;
}

export async function completeAppSessionSignOut(
  requestSignOut: () => Promise<void>,
  clearIdentityState: (next: "anonymous" | "probe") => Promise<void>,
): Promise<void> {
  try {
    await requestSignOut();
  } catch (error) {
    await clearIdentityState("probe");
    throw error;
  }
  await clearIdentityState("anonymous");
}

export type AppSessionProviderProps = {
  readonly children: ReactNode;
};

function readWorkspaceId(input: unknown): string | undefined {
  if (input === null || typeof input !== "object" || Array.isArray(input)) return undefined;
  const value = (input as Readonly<Record<string, unknown>>)["workspace"];
  const parsed = UuidSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export function AppSessionProvider({ children }: AppSessionProviderProps) {
  const queryClient = useQueryClient();
  const [generation, setGeneration] = useState(0);
  const [authInvalidated, setAuthInvalidated] = useState(false);
  const routeSearch = useRouterState({ select: (state) => state.location.search });
  const mismatchHandler = useRef<() => void>(() => undefined);
  const lifecycleGeneration = useRef(0);
  const realtimeStore = useMemo<AppRealtimeStore>(() => createAppRealtimeStore({
    queryClient,
    onIdentityMismatch: () => mismatchHandler.current(),
  }), [queryClient]);

  const requestedWorkspaceId = readWorkspaceId(routeSearch);

  const bootstrapKey = ["app", "bootstrap", generation] as const;
  const bootstrap = useQuery({
    queryKey: bootstrapKey,
    enabled: !authInvalidated,
    queryFn: async ({ signal }) => {
      const result = await hydrateAppSession({
        readSession: getBrowserSession,
        readIdentity: () => apiRequest(IdentityProjectionSchema, "/api/account", { signal }),
        readWorkspaces: () => apiRequest(WorkspaceListSchema, "/api/workspaces", { signal }),
        readReadiness: () => apiRequest(FocusReadinessProjectionSchema, "/api/focus/readiness", { signal }),
      }, requestedWorkspaceId);
      signal.throwIfAborted();
      await i18n.changeLanguage(result.identity.effective.uiLocale);
      document.documentElement.lang = result.identity.effective.uiLocale;
      document.documentElement.dataset["reducedMotion"] = result.identity.effective.reducedMotion ? "true" : "false";
      return result;
    },
  });

  const clearIdentityState = useCallback(async (next: "anonymous" | "probe") => {
    setAuthInvalidated(true);
    realtimeStore.disconnect();
    await queryClient.cancelQueries();
    queryClient.clear();
    setGeneration((current) => current + 1);
    setAuthInvalidated(next === "anonymous");
  }, [queryClient, realtimeStore]);

  const restartIdentity = useCallback(
    () => clearIdentityState("probe"),
    [clearIdentityState],
  );
  mismatchHandler.current = () => {
    void restartIdentity();
  };

  const authError = bootstrap.error instanceof ApiError && bootstrap.error.status === 401;
  useEffect(() => {
    if (!authError || authInvalidated) return;
    void clearIdentityState("anonymous");
  }, [authError, authInvalidated, clearIdentityState]);

  const bootstrapData = bootstrap.data ?? null;
  const identity = bootstrapData?.identity ?? null;
  const workspaces = bootstrapData?.workspaces.items ?? EMPTY_WORKSPACES;
  const selectedWorkspace = workspaces.find((workspace) => workspace.id === requestedWorkspaceId)
    ?? workspaces.find((workspace) => workspace.id === bootstrapData?.initiallySelectedWorkspaceId)
    ?? workspaces[0]
    ?? null;

  useEffect(() => {
    if (identity === null) return;
    const context: AppRealtimeContext = {
      actorAccountId: identity.actor.id,
      effectiveAccountId: identity.effective.id,
      simulationSessionId: identity.simulation?.id ?? null,
      workspaces: workspaces.map((workspace) => ({ workspaceId: workspace.id, lastSequence: "0" })),
    };
    realtimeStore.updateContext(context);
    realtimeStore.connect();
  }, [identity, realtimeStore, workspaces]);

  useEffect(() => {
    const currentGeneration = lifecycleGeneration.current + 1;
    lifecycleGeneration.current = currentGeneration;
    return () => {
      queueMicrotask(() => {
        if (lifecycleGeneration.current === currentGeneration) realtimeStore.dispose();
      });
    };
  }, [realtimeStore]);

  useEffect(() => {
    const effective = identity?.effective;
    if (effective === undefined) {
      delete document.documentElement.dataset["reducedMotion"];
      return;
    }
    document.documentElement.dataset["reducedMotion"] = effective.reducedMotion ? "true" : "false";
    document.documentElement.lang = effective.uiLocale;
    void i18n.changeLanguage(effective.uiLocale);
  }, [identity?.effective]);

  const preferenceMutation = useMutation({
    mutationFn: (command: PreferenceCommand) => apiRequest(AccountProjectionSchema, "/api/account/preferences", {
      method: "PATCH",
      body: command,
    }),
    onSuccess: (account) => {
      queryClient.setQueryData<AppBootstrap>(bootstrapKey, (current) => {
        if (current === undefined || current.identity.simulation !== null) return current;
        return {
          ...current,
          identity: {
            actor: account,
            effective: account,
            simulation: null,
          },
        };
      });
    },
  });
  const workspaceCreateMutation = useMutation({
    mutationFn: (command: CreateWorkspaceCommand) => apiRequest(
      WorkspaceProjectionSchema,
      "/api/workspaces",
      {
        method: "POST",
        body: command,
      },
    ),
    onSuccess: (workspace) => {
      queryClient.setQueryData<AppBootstrap>(bootstrapKey, (current) => (
        current === undefined ? current : mergeCreatedWorkspace(current, workspace)
      ));
    },
  });

  const createWorkspace = useCallback(
    (name: string) => workspaceCreateMutation.mutateAsync(createWorkspaceCommand(name)),
    [workspaceCreateMutation],
  );

  const updatePreferences = useCallback(async (
    uiLocale: AccountProjection["uiLocale"],
    reducedMotion: boolean,
  ) => {
    const effective = identity?.effective;
    if (effective === undefined || identity?.simulation !== null) {
      throw new Error("Account preferences require a real authenticated identity");
    }
    return preferenceMutation.mutateAsync(createPreferenceCommand(effective, uiLocale, reducedMotion));
  }, [identity, preferenceMutation]);

  const signOut = useCallback(
    () => completeAppSessionSignOut(signOutSession, clearIdentityState),
    [clearIdentityState],
  );

  const endSimulation = useCallback(async () => {
    setAuthInvalidated(true);
    realtimeStore.disconnect();
    try {
      await endSimulationSession();
    } catch (error) {
      await clearIdentityState("probe");
      throw error;
    }
    await clearIdentityState("probe");
  }, [clearIdentityState, realtimeStore]);

  let phase: AppSessionPhase;
  if (authInvalidated || authError) phase = "anonymous";
  else if (bootstrap.isPending) phase = "booting";
  else if (bootstrap.isError) phase = "error";
  else phase = "authenticated";

  const value = useMemo<AppSessionContextValue>(() => ({
    phase,
    identity,
    workspaces,
    selectedWorkspace,
    readiness: bootstrapData?.readiness ?? null,
    error: bootstrap.error,
    preferencePending: preferenceMutation.isPending,
    preferenceError: preferenceMutation.error,
    workspaceCreatePending: workspaceCreateMutation.isPending,
    workspaceCreateError: workspaceCreateMutation.error,
    restartIdentity,
    retry: async () => {
      await bootstrap.refetch();
    },
    createWorkspace,
    updatePreferences,
    signOut,
    endSimulation,
  }), [
    bootstrap,
    bootstrapData?.readiness,
    createWorkspace,
    endSimulation,
    identity,
    phase,
    preferenceMutation.error,
    preferenceMutation.isPending,
    restartIdentity,
    selectedWorkspace,
    signOut,
    updatePreferences,
    workspaces,
    workspaceCreateMutation.error,
    workspaceCreateMutation.isPending,
  ]);

  return (
    <AppRealtimeProvider store={realtimeStore}>
      <AppSessionContext.Provider value={value}>{children}</AppSessionContext.Provider>
    </AppRealtimeProvider>
  );
}
